import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ChevronRight, ChevronLeft, Loader2, Check } from 'lucide-react';
import { useShutterQuote } from './useShutterQuote';
import { STEPS } from './constants';
import StepDetails from './StepDetails';
import StepConfigure from './StepConfigure';
import StepSizes from './StepSizes';
import QuoteResult from './QuoteResult';

const ShutterQuote = () => {
  const {
    currentStep,
    setCurrentStep,
    isLoading,
    formData,
    quoteResult,
    updateFormData,
    canProceed,
    handleNext,
    handleBack,
    addWindow,
    removeWindow,
    updateWindow,
    resetQuote
  } = useShutterQuote();

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <StepDetails formData={formData} updateFormData={updateFormData} />;
      case 2:
        return <StepConfigure formData={formData} updateFormData={updateFormData} />;
      case 3:
        return (
          <StepSizes 
            formData={formData}
            updateFormData={updateFormData}
            addWindow={addWindow}
            removeWindow={removeWindow}
            updateWindow={updateWindow}
          />
        );
      case 4:
        return <QuoteResult result={quoteResult} onReset={resetQuote} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8faf9] to-white py-8 px-4" data-testid="shutter-quote">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        {currentStep < 4 && (
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Get Your Instant Shutter Quote
            </h1>
            <p className="text-gray-600">
              Enter your details and window sizes for a personalized quote
            </p>
          </div>
        )}

        {/* Progress Steps */}
        {currentStep < 4 && (
          <div className="flex items-center justify-between mb-8 px-4">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                      currentStep > step.number 
                        ? 'bg-[#8CB89F] text-white' 
                        : currentStep === step.number 
                          ? 'bg-[#8CB89F] text-white ring-4 ring-[#8CB89F]/20' 
                          : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {currentStep > step.number ? <Check className="w-5 h-5" /> : step.number}
                  </div>
                  <span className={`text-xs mt-1 ${currentStep >= step.number ? 'text-[#8CB89F] font-medium' : 'text-gray-400'}`}>
                    {step.name}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${currentStep > step.number ? 'bg-[#8CB89F]' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Step Content */}
        <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
          <CardContent className="p-6 md:p-8">
            {renderStep()}
            
            {/* Navigation Buttons */}
            {currentStep < 4 && (
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button 
                  variant="outline" 
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  className="flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
                
                <Button 
                  onClick={handleNext}
                  disabled={!canProceed() || isLoading}
                  className="flex items-center gap-2 bg-[#8CB89F] hover:bg-[#7aa88f]"
                  data-testid="continue-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : currentStep === 3 ? (
                    'Get Quote'
                  ) : (
                    <>
                      Continue
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ShutterQuote;
